import React, { useEffect, useMemo, useState } from 'react';
import { ApprovalTagItem } from '@/components/Approval/CreateApprovalModal/styles';
import { Stack } from '@mui/material';
import SubHeaderContent from '@/components/MobileApproval/SubHeaderContent';
import { FormProvider, useForm } from 'react-hook-form';
import CustomInput from '@/components/Organization/CustomInput';
import NoData from '@/components/Approval/@components/NoData';
import { useDebounce } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { useGetApprovalType } from '@/hooks/apiHooks';
import { categorizeByTag } from '@/components/MobileApproval/ApprovalWirte';
import { ApprovalType } from '@/types/api';
import { WorkEventName, WorkPageAreaType } from '@/constant/gtm';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';

type SelectApprovalTypeProps = {
  pageArea: WorkPageAreaType;
  onSelect: (id: number) => void;
  onOpenWorkingHoursChange?: () => void;
  onOpenOvertimeRequest?: () => void;
  onOpenOvertimeAdjust?: () => void;
  onOpenAnnualLeave?: () => void;
};

export type ApprovalTypeByTag = { menu: string; list: ApprovalType[] };

const SelectApprovalType = ({
  pageArea,
  onSelect,
  onOpenWorkingHoursChange,
  onOpenOvertimeRequest,
  onOpenOvertimeAdjust,
  onOpenAnnualLeave,
}: SelectApprovalTypeProps) => {
  const { t } = useTranslation();
  const workEventTracker = useWorkEventTracker(pageArea);

  const { data } = useGetApprovalType();
  const categorizedApprovalType = categorizeByTag(data);
  const approvalTags: ApprovalTypeByTag[] = useMemo(() => {
    return Object.entries(categorizedApprovalType).map(([key, value]) => ({
      menu: key,
      list: value,
    }));
  }, [categorizedApprovalType]);

  const methods = useForm<{ keyword: string }>({
    defaultValues: { keyword: '' },
  });
  const searchKeyword = methods.watch('keyword');
  const debouncedKeyword = useDebounce(searchKeyword, 500).replaceAll(' ', '');

  const [selectedIndex, setSelectedIndex] = useState(0);
  const filteredApprovalTags = useMemo(() => {
    const tag = !selectedIndex
      ? approvalTags.slice(1)
      : [approvalTags[selectedIndex]];

    if (!debouncedKeyword) return tag;

    return tag.reduce((prev, _tag) => {
      const list = _tag.list.filter((item) => {
        return item.name
          .toUpperCase()
          .replaceAll(' ', '')
          .includes(debouncedKeyword.toUpperCase());
      });

      if (list.length > 0) prev.push({ menu: _tag.menu, list });

      return prev;
    }, [] as ApprovalTypeByTag[]);
  }, [approvalTags, selectedIndex, debouncedKeyword]);

  useEffect(() => {
    if (!debouncedKeyword) return;
    // GA Work - Approval 34번
    workEventTracker(WorkEventName.searchKeyword, {
      area_name: '양식 목록',
      keyword: debouncedKeyword,
    });
  }, [debouncedKeyword]);

  const handleClickTag = (id: number) => {
    const tag = approvalTags[id];
    if (!tag) return;

    setSelectedIndex(id);

    // GA Work - Approval 33번
    workEventTracker(WorkEventName.clickEvent, {
      area_name: '양식 목록',
      contents: tag.menu,
      status: true,
      description: '품의 양식 카테고리 선택',
    });
  };

  return (
    <>
      <Stack
        direction={'row'}
        flexWrap={'wrap'}
        gap={'8px'}
        sx={{
          position: 'sticky',
          top: '-32px',
          padding: '12px 0',
          backgroundColor: 'var(--dk-bg, white)',
          zIndex: 1,
        }}
      >
        {approvalTags.map(({ menu }, idx) => (
          <SubHeaderContent
            key={`menuList-${menu}`}
            id={idx}
            title={menu}
            clicked={selectedIndex === idx}
            handleClick={handleClickTag}
            style={{ padding: '8px 16px', fontSize: '16px', cursor: 'pointer' }}
            darkMode
          />
        ))}
      </Stack>

      <Stack direction={'row'} justifyContent={'end'} sx={{ width: '100%' }}>
        <FormProvider {...methods}>
          <CustomInput
            width={'330px'}
            placeHolder={t('양식명을 검색해 주세요.')}
          />
        </FormProvider>
      </Stack>

      <Stack
        direction={'row'}
        flexWrap={'wrap'}
        sx={{
          width: '100%',
          marginTop: '20px',
          overflowY: 'auto',
        }}
      >
        {filteredApprovalTags.length > 0
          ? filteredApprovalTags.map((item) => {
              return (
                <ApprovalTagItem
                  pageArea={pageArea}
                  key={`${item.menu}`}
                  title={item.menu}
                  approvalList={item.list}
                  onClickDoc={onSelect}
                  onOpenWorkingHoursChange={onOpenWorkingHoursChange}
                  onOpenOvertimeRequest={onOpenOvertimeRequest}
                  onOpenOvertimeAdjust={onOpenOvertimeAdjust}
                  onOpenAnnualLeave={onOpenAnnualLeave}
                />
              );
            })
          : approvalTags.length > 0 && (
              <NoData
                title={
                  <>
                    {t('검색어와 일치하는 문서가 없어요.')}
                    <br />
                    {t('검색어를 다시 한번 확인해 주세요.')}
                  </>
                }
              />
            )}
      </Stack>
    </>
  );
};

export default SelectApprovalType;
